import { useEffect, useRef, useState } from 'react';
import coachCopy from '@/data/classCoach.json';
import { CLASS_GROUPS } from '@/state/classSession';

const TIERS = ['warn', 'roast', 'heavy'];
const SEVERITY = { warn: 1, roast: 2, heavy: 3 };

export function pickCounts(tracks = []) {
  const counts = Object.fromEntries(CLASS_GROUPS.map((group) => [group, 0]));
  tracks.forEach((track) => {
    const group = track.classSlot?.group;
    if (group in counts && !track.muted && !track.inBank) counts[group] += 1;
  });
  return counts;
}

export function initialCoachState() {
  return {
    lastFired: Object.fromEntries(CLASS_GROUPS.map((group) => [group, 0])),
    armed: Object.fromEntries(CLASS_GROUPS.map((group) => [group, false])),
    rotation: Object.fromEntries(CLASS_GROUPS.map((group) => [group, {
      warn: 0, roast: 0, heavy: 0, flowers: 0,
    }])),
    event: null,
  };
}

export function tierForCount(group, count, data = coachCopy) {
  const thresholds = data.thresholds?.[group] || {};
  return TIERS.reduce((tier, candidate) => (
    Number.isFinite(Number(thresholds[candidate])) && count >= Number(thresholds[candidate])
      ? candidate : tier
  ), null);
}

const firstThreshold = (group, data) => {
  const values = Object.values(data.thresholds?.[group] || {}).map(Number).filter(Number.isFinite);
  return values.length ? Math.min(...values) : Infinity;
};

const rotatedLine = (lines, index) => lines?.length ? lines[index % lines.length] : '';

/** Pure AD7 transition. The returned event remains visible until replaced or dismissed. */
export function coachReducer(state = initialCoachState(), prevCounts = {}, nextCounts = {}, data = coachCopy) {
  const next = {
    ...state,
    lastFired: { ...state.lastFired },
    armed: { ...state.armed },
    rotation: Object.fromEntries(CLASS_GROUPS.map((group) => [group, { ...state.rotation[group] }])),
  };
  const rises = [];
  const flowers = [];

  CLASS_GROUPS.forEach((group) => {
    const before = Number(prevCounts[group] || 0);
    const after = Number(nextCounts[group] || 0);
    const threshold = firstThreshold(group, data);
    const tier = tierForCount(group, after, data);

    if (after >= threshold && after > Number(next.lastFired[group] || 0) && after > before && tier) {
      const index = Number(next.rotation[group][tier] || 0);
      rises.push({
        kind: tier,
        group,
        count: after,
        pose: tier === 'warn' ? 'confused' : (tier === 'heavy' ? 'construction' : 'thinking'),
        line: rotatedLine(data.roasts?.[group]?.[tier], index),
      });
      next.lastFired[group] = after;
      next.armed[group] = true;
      next.rotation[group][tier] = index + 1;
    }

    if (next.armed[group] && after < threshold && before >= threshold) {
      const index = Number(next.rotation[group].flowers || 0);
      flowers.push({
        kind: 'flowers', group, count: after, pose: 'happy',
        line: rotatedLine(data.flowers, index),
      });
      next.lastFired[group] = 0;
      next.armed[group] = false;
      next.rotation[group].flowers = index + 1;
    }
  });

  if (rises.length) {
    next.event = rises.sort((a, b) => SEVERITY[b.kind] - SEVERITY[a.kind]
      || CLASS_GROUPS.indexOf(a.group) - CLASS_GROUPS.indexOf(b.group))[0];
  } else if (flowers.length) {
    next.event = flowers[0];
  }
  return next;
}

/**
 * Runs coachReducer on every arrangement change (undo/redo included). The previous counts are
 * captured before the state update: an updater reading the ref later saw the new counts twice,
 * so Pizzul never fired in the app even though the reducer's own tests passed.
 */
export function useClassCoachController(active, tracks) {
  const [state, setState] = useState(initialCoachState);
  const previousCountsRef = useRef(pickCounts(tracks));
  useEffect(() => {
    if (!active) return;
    const prevCounts = previousCountsRef.current;
    const nextCounts = pickCounts(tracks);
    previousCountsRef.current = nextCounts;
    setState((current) => coachReducer(current, prevCounts, nextCounts));
  }, [active, tracks]);
  return [state, setState];
}

export const dismissCoachEvent = (state) => ({ ...state, event: null });

export { coachCopy };
